import React, { useMemo, useState, useEffect, useRef } from "react";
import { Modal, ModalBody } from "reactstrap";
import { withTranslation } from "react-i18next";
import { useQueryClient } from "react-query";
import crossIcon from "assets/images/cross.svg";
import Text from "components/Text";
import Input from "components/Input";
import ReactDatePickerCommon from "components/ReactDatePickerCommon";
import DeleteIcon from "assets/images/delete-red.svg";
import styles from "./profile.module.scss";
import useUploadService from "hooks/useUploadService";
import constants, { CREDENTIAL_STATUS } from "../../../../constants";
import toast, { LoaderIcon } from "react-hot-toast";
import { v4 as uuidv4 } from "uuid";
import moment from "moment";
import CustomDropdown from "components/Dropdown";
import { useAccountOwnerCommon } from "lib/Context/AccountOwner";
import { useGetAdminStaffOfficeList } from "repositories/office-repository";
import { useGetUniqueStaffsUnderOwner } from "repositories/office-repository";
import {
  useCreateCredentialLicenseMutation,
  useUpdateCredentialLicenseMutation,
} from "repositories/user-repository";
import SelectCredentialNameModal from "features/credentials/SelectCredentialNameModal";
import {
  CREDENTIAL_TYPE,
  getCredentialTypeLabel,
} from "features/credentials/credentialTypes";
import {
  getCredentialDisplayName,
  getCredentialMasterIdFromRecord,
} from "features/credentials/utils/normalizeCredentialMaster";

/**
 * CredentialExpiryType API values, sent as `ExpiryType`.
 */
export const CREDENTIAL_EXPIRY_TYPE = {
  EXPIRES_ON_DATE: 1,
  DOES_NOT_EXPIRE: 2,
  EXPIRY_UNKNOWN: 3,
};

/**
 * Resolves the expiry choice to pre-select when editing a credential.
 * Records saved before `expiryType` existed fall back to their computed
 * status, then to the presence of an expiry date.
 * @param {{ expiryType?: number|string, status?: number|string, expiryDate?: string|null }} credential
 * @returns {number} one of CREDENTIAL_EXPIRY_TYPE
 */
export const getExpiryType = (credential) => {
  const expiryType =
    credential?.expiryType != null ? +credential.expiryType : null;
  if (
    Object.values(CREDENTIAL_EXPIRY_TYPE).some((type) => type === expiryType)
  ) {
    return expiryType;
  }

  const status = credential?.status != null ? +credential.status : null;
  if (status === CREDENTIAL_STATUS.NO_EXPIRY) {
    return CREDENTIAL_EXPIRY_TYPE.DOES_NOT_EXPIRE;
  }
  if (status === CREDENTIAL_STATUS.EXPIRY_UNKNOWN) {
    return CREDENTIAL_EXPIRY_TYPE.EXPIRY_UNKNOWN;
  }

  return credential?.expiryDate
    ? CREDENTIAL_EXPIRY_TYPE.EXPIRES_ON_DATE
    : CREDENTIAL_EXPIRY_TYPE.EXPIRY_UNKNOWN;
};

/**
 * Converts an API date to a Date object, or null when missing/invalid.
 * @param {string|Date|null|undefined} value
 * @returns {Date|null}
 */
const toOptionalDate = (value) => {
  if (!value) {
    return null;
  }
  const parsed = moment(value);
  return parsed.isValid() ? parsed.toDate() : null;
};

const EXPIRY_TYPE_OPTIONS = [
  {
    id: CREDENTIAL_EXPIRY_TYPE.EXPIRES_ON_DATE,
    translationKey: "accountOwner.expiresOnDate",
  },
  {
    id: CREDENTIAL_EXPIRY_TYPE.DOES_NOT_EXPIRE,
    translationKey: "accountOwner.doesNotExpire",
  },
  {
    id: CREDENTIAL_EXPIRY_TYPE.EXPIRY_UNKNOWN,
    translationKey: "accountOwner.expiryUnknownStatus",
  },
];
export const userCertificateContainerName = "user-license-certificate";
export const officeCertificateContainerName = "office-license-certificate";
const allowedTypes = constants.chat.allowedTypesForImageDocsAndPdf;
const CommonFileSelectionAndDelete = ({
  handleRemoveFile,
  handleFileChange,
  state,
  docType,
  labelHeading,
  isMultiple = true,
  errorMessage,
  t,
}) => {
  const filesData = state[docType].filter((file) => {
    if (file.IsDeleted || file.isDeleted) {
      return false;
    }
    if (file.file) {
      return true;
    }
    return typeof file.path === "string" && file.path.trim().length > 0;
  });
  return (
    <div
      className={`d-flex flex-column margin-bottom-30 ${styles["file-section"]}`}
    >
      <div className="d-flex justify-content-between">
        <Text size="13px" weight="400" color="#79869A" lineHeight="14px">
          {labelHeading}
        </Text>
        <label
          htmlFor={docType + "fileInput"}
          className={styles["appointment-link"]}
        >
          <input
            name="uploadFile"
            type="file"
            id={docType + "fileInput"}
            className="d-none"
            onChange={handleFileChange.bind(this, docType)}
            multiple={isMultiple}
          />
          {t("accountOwner.uploadFile")}
        </label>
      </div>
      <div className={` ${styles["proof-documents-div"]}`}>
        {filesData.length === 0 ? (
          <Text size="14px" weight="600" color="#2D3245" lineHeight="20px">
            {t("accountOwner.noFileSelected")}{" "}
          </Text>
        ) : (
          <ul style={{ paddingLeft: 0, margin: 0, width: "100%" }}>
            {filesData.map((file, idx) => {
              const path = typeof file.path === "string" ? file.path : "";
              const isApiFile =
                !file.file &&
                path &&
                (path.startsWith("http") ||
                  path.startsWith("policy-documents/"));
              const fileUrl = path
                ? path.startsWith("http")
                  ? path
                  : `https://${process.env.REACT_APP_AZURE_STORAGE_ACCOUNT}.blob.core.windows.net/${path}`
                : "#";
              const fileNameDisplay =
                file.file?.name ||
                (path
                  ? path.split("/").pop()
                  : t("accountOwner.unknownFile") || "Unknown file");

              return (
                <li
                  key={file.uniqueKey}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  {isApiFile ? (
                    <a
                      href={fileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ marginRight: 8 }}
                    >
                      {fileNameDisplay}
                    </a>
                  ) : (
                    <span style={{ marginRight: 8 }} className="break_all">
                      {fileNameDisplay}
                    </span>
                  )}
                  <img
                    src={DeleteIcon}
                    alt="DeleteIcon"
                    style={{ cursor: "pointer" }}
                    onClick={() => handleRemoveFile(docType, idx)}
                  />
                </li>
              );
            })}
          </ul>
        )}
      </div>{" "}
      {errorMessage && <span className="error-msg">{errorMessage}</span>}
    </div>
  );
};
function AddEditLincenseeModal({
  isModalOpen,
  closeModal,
  t,
  heading,
  addStaffOffice,
  editData,
  refetchData,
  selectedOfficeFilter = [],
  isFromProfile = false,
}) {
  const officeCredentialEnabled = useMemo(
    () => !!addStaffOffice && addStaffOffice.includes("office_credential"),
    [],
  );
  const editEnabled = useMemo(() => heading.includes("edit"), []);
  const [state, setState] = useState(() => {
    const isEdit = heading.includes("edit");
    return {
      docs:
        isEdit &&
        typeof editData?.proofDocumentPath === "string" &&
        editData.proofDocumentPath.trim()
          ? [
              {
                uniqueKey: uuidv4(),
                path: editData.proofDocumentPath,
                isDeleted: false,
              },
            ]
          : [],
      additionalDocs: editData?.additionalDocuments
        ? editData?.additionalDocuments.map((res) => ({
            uniqueKey: res.id,
            id: res.id,
            path: res.documentPath,
            isDeleted: false,
          }))
        : [],
      certification_name: isEdit ? getCredentialDisplayName(editData) : "",
      credentialMasterId: isEdit
        ? getCredentialMasterIdFromRecord(editData)
        : "",
      certification_type: isEdit ? editData.credentialType : "",
      certification_issuing_authority: isEdit
        ? editData.issuingAuthority || ""
        : "",
      certification_credential_ID: isEdit ? editData.credentialId || "" : "",
      issueDate: isEdit ? toOptionalDate(editData.issueDate) : null,
      expiryType: isEdit
        ? getExpiryType(editData)
        : CREDENTIAL_EXPIRY_TYPE.EXPIRY_UNKNOWN,
      expiryDate:
        isEdit &&
        getExpiryType(editData) === CREDENTIAL_EXPIRY_TYPE.EXPIRES_ON_DATE
          ? toOptionalDate(editData.expiryDate)
          : null,
      office_id_selection:
        editEnabled && addStaffOffice && editData.officeId
          ? editData.officeId
          : "",
      staff_id_selection:
        editEnabled && addStaffOffice && editData.userId ? editData.userId : "",
    };
  });
  const {
    selectedId,
    profile,
    isStaff,
    isOwner,
    isAdmin,
    dataAccessValues,
    commonOwnerData,
  } = useAccountOwnerCommon();
  const [isCredentialNameModalOpen, setIsCredentialNameModalOpen] =
    useState(false);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const hasSubmittedWithErrors = useRef(false);
  const isFormValid = useRef(false);
  const validationTimeoutRef = useRef(null);
  const queryClient = useQueryClient();
  const createMutation = useCreateCredentialLicenseMutation();
  const updateMutation = useUpdateCredentialLicenseMutation();
  const { data: officeListData, isFetching: isOfficeListFetching } =
    useGetAdminStaffOfficeList(selectedId, {
      enabled: !!addStaffOffice && !!selectedId,
    });
  const { data: staffListData, isFetching: isStaffListFetching } =
    useGetUniqueStaffsUnderOwner(selectedId, {
      enabled:
        addStaffOffice &&
        addStaffOffice.includes("staff_credential") &&
        !!selectedId,
    });
  // Staff options are derived from both queries, so the dropdown stays in a loading state until both settle
  const isOfficeStaffOptionsLoading = officeCredentialEnabled
    ? isOfficeListFetching
    : isStaffListFetching || isOfficeListFetching;

  const { upload: uploadImage } = useUploadService();

  // Reset error tracking when modal closes
  useEffect(() => {
    if (!isModalOpen) {
      hasSubmittedWithErrors.current = false;
      isFormValid.current = false;
      setErrors({});
      setIsCredentialNameModalOpen(false);
      // Clear any pending validation timeout
      if (validationTimeoutRef.current) {
        clearTimeout(validationTimeoutRef.current);
        validationTimeoutRef.current = null;
      }
    }
    // Cleanup on unmount
    return () => {
      if (validationTimeoutRef.current) {
        clearTimeout(validationTimeoutRef.current);
        validationTimeoutRef.current = null;
      }
    };
  }, [isModalOpen]);

  // Debounced validation function
  const debouncedValidate = (values) => {
    // Clear existing timeout
    if (validationTimeoutRef.current) {
      clearTimeout(validationTimeoutRef.current);
    }
    // Set new timeout
    validationTimeoutRef.current = setTimeout(() => {
      isVerifiedForm(values);
      validationTimeoutRef.current = null;
    }, 300); // 300ms debounce delay
  };

  const isVerifiedForm = (values) => {
    const errors = {};
    const stateValues = values || state;
    let verified = true;
    if (addStaffOffice) {
      if (officeCredentialEnabled && !stateValues.office_id_selection) {
        errors.officeStaffError = t(
          "accountOwner.pleaseSelectAtLeastOneOffice",
        );
        verified = false;
      } else if (!officeCredentialEnabled && !stateValues.staff_id_selection) {
        errors.officeStaffError = t(
          "accountOwner.pleaseSelectAtLeastOneStaffMember",
        );
        verified = false;
      }
      if (!stateValues.certification_type) {
        errors.typeError = t("accountOwner.pleaseSelectAtLeastType");
        verified = false;
      }
    }
    const certificateName = (stateValues.certification_name || "").trim();
    if (!stateValues.credentialMasterId || !certificateName.length) {
      errors.certificateName = t("accountOwner.pleaseAddCredentialName");
      verified = false;
    }
    const authorityName = (
      stateValues.certification_issuing_authority || ""
    ).trim();
    if (authorityName.length > 100) {
      errors.authorityName = t("form.errors.maxLimit", { limit: 100 });
      verified = false;
    }

    if ((stateValues.certification_credential_ID || "").trim().length > 100) {
      errors.credential_ID = t("form.errors.maxLimit", { limit: 100 });
      verified = false;
    }
    if (stateValues.expiryType === CREDENTIAL_EXPIRY_TYPE.EXPIRES_ON_DATE) {
      if (!stateValues.expiryDate) {
        errors.expiryDate = t("accountOwner.expiryDateRequired");
        verified = false;
      } else if (
        stateValues.issueDate &&
        moment(stateValues.expiryDate)
          .startOf("day")
          .isSameOrBefore(moment(stateValues.issueDate).startOf("day"))
      ) {
        errors.expiryDate = t(
          "accountOwner.expiryDateShouldBeGreaterThanIssueDate",
        );
        verified = false;
      }
    }
    setErrors(errors);
    isFormValid.current = verified;
    return verified;
  };
  const handleRemoveFile = (type, index) => {
    setState((prev) => {
      prev[type] = prev[type].map((_, i) => {
        if (_.path.includes("http://")) {
          URL.revokeObjectURL(_.path);
        }
        if (index === i) {
          _.isDeleted = true;
        }
        return _;
      });
      // Re-validate if form was previously submitted with errors
      if (hasSubmittedWithErrors.current && !isFormValid.current) {
        debouncedValidate({ ...prev });
      }
      return { ...prev };
    });
  };

  const handleFileChange = (type, event) => {
    if (!event || !event.target) return;
    const files = event.target.files;
    if (!files.length) return;

    const newFiles = [];
    for (let i = 0; i < files.length; i++) {
      if (!allowedTypes.includes(files[i]?.type)) {
        toast.error(t("fileNotSupported"));
        continue;
      }

      if (files[i]?.size > constants.MAX_FILE_SIZE_5_MB) {
        toast.error(`${t("form.errors.fileSize")}`);
        continue;
      }

      newFiles.push({
        path: URL.createObjectURL(files[i]),
        file: files[i],
        uniqueKey: uuidv4(),
        isDeleted: false,
      });
    }

    setState((prev) => {
      let selectedValues;
      // For "docs" type, replace existing files (only one file allowed)
      // For other types, append to existing files (multiple files allowed)
      if (type === "docs") {
        selectedValues = { ...prev, [type]: newFiles };
      } else {
        selectedValues = { ...prev, [type]: [...prev[type], ...newFiles] };
      }
      // Re-validate if form was previously submitted with errors
      if (hasSubmittedWithErrors.current && !isFormValid.current) {
        debouncedValidate(selectedValues);
      }
      return selectedValues;
    });
    event.target.value = "";
  };
  const onInputChangeHandler = (e) => {
    const { name, value } = e.target;
    setState((prev) => {
      const values = { ...prev, [name]: value };
      // Re-validate only if form was previously submitted with errors
      // Stop validating once form becomes valid
      if (hasSubmittedWithErrors.current && !isFormValid.current) {
        debouncedValidate(values);
      } else if (isFormValid.current) {
        // Clear errors when form is valid and user makes changes
        setErrors({});
      }
      return values;
    });
  };
  const onExpiryTypeChange = (expiryType) => {
    setState((prev) => {
      const values = {
        ...prev,
        expiryType,
        expiryDate:
          expiryType === CREDENTIAL_EXPIRY_TYPE.EXPIRES_ON_DATE
            ? prev.expiryDate
            : null,
      };

      if (hasSubmittedWithErrors.current) {
        debouncedValidate(values);
      }
      return values;
    });
  };
  const onSubmitHandler = async (e) => {
    e.preventDefault();
    const isValid = isVerifiedForm();
    if (!isValid) {
      hasSubmittedWithErrors.current = true;
      return;
    }

    // Form is valid, reset error tracking
    hasSubmittedWithErrors.current = false;
    setIsSubmitting(true);
    const containerName = officeCredentialEnabled
      ? officeCertificateContainerName
      : userCertificateContainerName;
    const folderName = officeCredentialEnabled
      ? `office/${state.office_id_selection}`
      : `user/${addStaffOffice ? state.staff_id_selection : profile.id}`;
    try {
      // Upload proof document
      let proofDocumentPath = "";
      const proofDoc = state.docs.find((doc) => !doc.isDeleted);
      if (proofDoc) {
        if (proofDoc.file) {
          // New file - upload it
          const [, uploadedPath] = await uploadImage(
            proofDoc.file,
            containerName,
            () => {},
            () => {},
            folderName,
          );
          proofDocumentPath = `${containerName}/${uploadedPath.blobName}`;
        } else if (proofDoc.path && !proofDoc.path.startsWith("blob:")) {
          // Existing file from API - use the path as is
          proofDocumentPath = `${containerName}/${proofDoc.path.split(containerName).pop()}`;
        }
      }

      // Upload additional documents
      const additionalDocuments = [];
      for (const doc of state.additionalDocs) {
        if (doc.isDeleted && editEnabled && doc.id) {
          // In edit mode, include deleted documents with their id so backend can delete them
          additionalDocuments.push({
            id: doc.id,
            documentPath: doc.path,
            isDeleted: true,
          });
        } else if (!doc.isDeleted) {
          if (doc.file) {
            // New file - upload it
            const [, uploadedPath] = await uploadImage(
              doc.file,
              containerName,
              () => {},
              () => {},
              folderName,
            );
            additionalDocuments.push({
              documentPath: `${containerName}/${uploadedPath.blobName}`,
            });
          } else if (doc.path && !doc.path.startsWith("blob:")) {
            additionalDocuments.push({
              id: doc.id,
              documentPath: `${containerName}/${doc.path.split(containerName).pop()}`,
              isDeleted: false,
            });
          }
        }
      }

      // Prepare the data payload
      const payload = {
        UserId: !officeCredentialEnabled
          ? addStaffOffice
            ? state.staff_id_selection
            : profile.id
          : null,
        OfficeId: officeCredentialEnabled ? state.office_id_selection : null,
        OwnerId: selectedId,
        CredentialMasterId: state.credentialMasterId,
        CredentialName: (state.certification_name || "").trim(),
        CredentialType: state.certification_type,
        Type: officeCredentialEnabled ? 2 : 1,
        issuingAuthority:
          (state.certification_issuing_authority || "").trim() || null,
        credentialId: (state.certification_credential_ID || "").trim() || null,
        issueDate: state.issueDate
          ? moment(state.issueDate).format("YYYY-MM-DD")
          : null,
        ExpiryType: state.expiryType,
        expiryDate:
          state.expiryType === CREDENTIAL_EXPIRY_TYPE.EXPIRES_ON_DATE &&
          state.expiryDate
            ? moment(state.expiryDate).format("YYYY-MM-DD")
            : null,
        proofDocumentPath: proofDocumentPath,
        additionalDocuments: additionalDocuments,
      };

      if (editEnabled && editData?.id) {
        // Update existing credential
        payload.id = editData.id;
        await updateMutation.mutateAsync(payload);
        toast.success(t("accountOwner.credentialUpdatedSuccessfully"));
      } else {
        // Create new credential
        await createMutation.mutateAsync(payload);
        toast.success(t("accountOwner.credentialSavedSuccessfully"));
      }

      // Invalidate queries to refresh the list
      queryClient.invalidateQueries(["user", profile?.id]);
      if (refetchData) {
        refetchData();
      }
      // Reset error tracking on successful submission
      hasSubmittedWithErrors.current = false;
      isFormValid.current = false;
      setErrors({});
      // Close modal
      closeModal();
    } catch (error) {
      toast.error(
        error.message || t("somethingWentWrong") || "Something went wrong",
      );
    } finally {
      setIsSubmitting(false);
    }
  };
  const customDropDownValues = useMemo(() => {
    if (addStaffOffice) {
      if (officeCredentialEnabled && officeListData?.data) {
        return officeListData.data.map((res) => ({
          id: res.id,
          name: res.name,
        }));
      } else if (!officeCredentialEnabled && staffListData?.data) {
        const officeList = new Map();
        if (officeListData?.data.length) {
          officeListData?.data.forEach((res) => {
            officeList.set(res.id, res.name);
          });
        }
        return staffListData.data.reduce(
          (prev, curr) => {
            if (
              prev &&
              selectedId !== curr.id &&
              curr.userOffice.some(
                (res) => res.officeId && officeList.has(res.officeId),
              )
            ) {
              prev.push({
                id: curr.id,
                name: curr.firstName + " " + (curr.lastName || ""),
              });
            }
            return prev;
          },
          isStaff
            ? [
                {
                  id: commonOwnerData?.id,
                  name:
                    commonOwnerData?.firstName +
                    (commonOwnerData?.lastName
                      ? " " + commonOwnerData?.lastName
                      : ""),
                },
              ]
            : [],
        );
      }
    }
    return [];
  }, [
    staffListData?.data,
    officeListData?.data,
    addStaffOffice,
    officeCredentialEnabled,
    selectedId,
  ]);

  const canManageCredentialNames =
    isOwner || isAdmin || !!dataAccessValues?.isAdmin;
  const selectedCredential = useMemo(
    () =>
      state.credentialMasterId
        ? {
            id: state.credentialMasterId,
            credentialName: state.certification_name,
            type: state.certification_type,
          }
        : null,
    [
      state.credentialMasterId,
      state.certification_name,
      state.certification_type,
    ],
  );
  const formDisbaled =
    isSubmitting || createMutation.isLoading || updateMutation.isLoading;
  const handleCredentialNameSelect = (item) => {
    setState((prev) => {
      const values = {
        ...prev,
        credentialMasterId: item.id,
        certification_name: item.credentialName,
        certification_type: item.type,
      };
      if (hasSubmittedWithErrors.current && !isFormValid.current) {
        debouncedValidate(values);
      } else if (isFormValid.current) {
        setErrors({});
      }
      return values;
    });
  };
  const handleCredentialNameUpdated = (item) => {
    if (String(state.credentialMasterId) !== String(item.id)) {
      return;
    }
    setState((prev) => ({
      ...prev,
      certification_name: item.credentialName,
      certification_type: item.type,
    }));
  };
  const handleCredentialNameDeleted = (id) => {
    if (String(state.credentialMasterId) !== String(id)) {
      return;
    }
    setState((prev) => ({
      ...prev,
      credentialMasterId: "",
      certification_name: "",
      certification_type: "",
    }));
  };
  return (
    <>
      <Modal
        isOpen={isModalOpen}
        toggle={closeModal}
        className="modal-dialog-centered confirm-contract-modal"
        modalClassName="custom-modal"
      >
        <span className="close-btn" onClick={closeModal}>
          <img src={crossIcon} alt="close" />
        </span>
        <ModalBody>
          <Text size="25px" marginBottom="10px" weight="500" color="#111b45">
            <span className="modal-title-25 text-capitalize">
              {/* {heading} */}
              {officeCredentialEnabled
                ? t(
                    `accountOwner.${editEnabled ? "editOfficeCredential" : "addOfficeCredential"}`,
                  )
                : t(
                    "accountOwner." +
                      (editEnabled
                        ? "editStaffCredential"
                        : "addStaffCredential"),
                  )}
            </span>
          </Text>
          <Text size="16px" marginBottom="25px" weight="300" color="#535b5f">
            {officeCredentialEnabled
              ? t("accountOwner.addCredentialInformationForOffice", {
                  addEdit: editEnabled ? "Edit" : "Add",
                })
              : t("accountOwner.addCredentialInformationForStaff", {
                  addEdit: editEnabled ? "Edit" : "Add",
                })}
          </Text>
          <form className="form-box" onSubmit={onSubmitHandler}>
            <div className="field-group">
              {addStaffOffice && (
                <div className="c-field mb-3">
                  <label>
                    {officeCredentialEnabled
                      ? t("accountOwner.selectOffice")
                      : t("accountOwner.selectStaff")}
                    <span className={styles["required-mark"]}>*</span>
                  </label>
                  <div
                    className="custom-dropdown-only"
                    style={
                      editData ? { pointerEvents: "none", opacity: 0.5 } : {}
                    }
                  >
                    <CustomDropdown
                      options={customDropDownValues}
                      isLoading={isOfficeStaffOptionsLoading}
                      selectedOption={
                        officeCredentialEnabled
                          ? state.office_id_selection
                          : state.staff_id_selection
                      }
                      disabled={formDisbaled || !!editData}
                      selectOption={(id) => {
                        if (formDisbaled && !!editData) {
                          return;
                        }
                        onInputChangeHandler({
                          target: {
                            name: officeCredentialEnabled
                              ? "office_id_selection"
                              : "staff_id_selection",
                            value: id,
                          },
                        });
                      }}
                      defaultValue={
                        officeCredentialEnabled
                          ? editData?.officeName
                            ? editData?.officeName
                            : t("accountOwner.selectOffice")
                          : editData?.userName
                            ? editData?.userName
                            : t("accountOwner.selectStaff")
                      }
                    />
                  </div>
                  {errors?.officeStaffError && (
                    <span className="error-msg">{errors.officeStaffError}</span>
                  )}
                </div>
              )}
              <div className="c-field mb-3">
                <label>
                  {t("accountOwner.credentialName")}
                  <span className={styles["required-mark"]}>*</span>
                </label>
                <div
                  className={`c-form-control ${styles["clickable-field"]}`}
                  onClick={() => {
                    if (!formDisbaled) {
                      setIsCredentialNameModalOpen(true);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (!formDisbaled && (e.key === "Enter" || e.key === " ")) {
                      e.preventDefault();
                      setIsCredentialNameModalOpen(true);
                    }
                  }}
                  role="button"
                  tabIndex={formDisbaled ? -1 : 0}
                >
                  <span>
                    {state.certification_name ||
                      t("accountOwner.noNameSelected")}
                  </span>
                </div>
                {errors.certificateName && (
                  <span className="error-msg">{errors.certificateName}</span>
                )}
              </div>
              <Input
                Title={t("accountOwner.type")}
                Type="text"
                Placeholder={t("form.placeholder1", {
                  field: "",
                })}
                Name="certification_type"
                Id="certification_type"
                ReadOnly={true}
                Disabled={true}
                Classes={styles["disabled-type-field"]}
                Value={
                  getCredentialTypeLabel(state.certification_type, t) ||
                  t("accountOwner.na")
                }
                Error={errors?.typeError}
              />
              <Input
                Title={t("accountOwner.issuingAuthority")}
                Type="text"
                Placeholder={t("form.placeholder1", {
                  field: "",
                })}
                MaxLength={100}
                Name="certification_issuing_authority"
                Id="certification_issuing_authority"
                Value={state.certification_issuing_authority}
                HandleChange={onInputChangeHandler}
                Error={errors.authorityName}
              />
              <Input
                Title={t("accountOwner.credentialIdOptional")}
                Type="text"
                Placeholder={t("form.placeholder1", {
                  field: "",
                })}
                MaxLength={100}
                Name="certification_credential_ID"
                Id="certification_credential_ID"
                Value={state.certification_credential_ID}
                HandleChange={onInputChangeHandler}
                Error={errors.credential_ID}
              />
              <div className="c-field w-100">
                <label>{t("accountOwner.issueDate")}</label>
                <div className="d-flex inputdate">
                  <ReactDatePickerCommon
                    dateFormat="dd-MM-yyyy"
                    className="c-form-control"
                    onChange={(e) => {
                      onInputChangeHandler({
                        target: {
                          name: "issueDate",
                          value: e,
                        },
                      });
                    }}
                    maxDate={new Date()}
                    selected={state.issueDate || null}
                  />
                </div>
              </div>
              <fieldset className={styles["expiry-section"]}>
                <legend>{t("accountOwner.expiryDate")}</legend>
                <div className={styles["expiry-box"]}>
                  <div className={styles["expiry-options"]}>
                    {EXPIRY_TYPE_OPTIONS.map((option) => (
                      <div className="ch-radio" key={option.id}>
                        <label>
                          <input
                            type="radio"
                            name="expiryType"
                            value={option.id}
                            checked={state.expiryType === option.id}
                            onChange={() => onExpiryTypeChange(option.id)}
                            disabled={formDisbaled}
                          />
                          <span>{t(option.translationKey)}</span>
                        </label>
                      </div>
                    ))}
                  </div>
                  {state.expiryType ===
                    CREDENTIAL_EXPIRY_TYPE.EXPIRES_ON_DATE && (
                    <div
                      className={`c-field w-100 ${styles["expiry-date-field"]}`}
                    >
                      <label>{t("accountOwner.expiryDate")}</label>
                      <div className="d-flex inputdate">
                        <ReactDatePickerCommon
                          dateFormat="dd-MM-yyyy"
                          className="c-form-control"
                          onChange={(e) => {
                            onInputChangeHandler({
                              target: {
                                name: "expiryDate",
                                value: e,
                              },
                            });
                          }}
                          minDate={
                            state.issueDate
                              ? moment(state.issueDate).add(1, "day").toDate()
                              : undefined
                          }
                          selected={state.expiryDate || null}
                        />
                      </div>
                      {errors?.expiryDate && (
                        <span className="error-msg">{errors.expiryDate}</span>
                      )}
                    </div>
                  )}
                </div>
              </fieldset>
              <CommonFileSelectionAndDelete
                docType="docs"
                handleFileChange={handleFileChange}
                handleRemoveFile={handleRemoveFile}
                labelHeading={t("accountOwner.proofDocument")}
                state={state}
                isMultiple={false}
                errorMessage={errors.docs}
                t={t}
              />
              <CommonFileSelectionAndDelete
                docType="additionalDocs"
                handleFileChange={handleFileChange}
                handleRemoveFile={handleRemoveFile}
                labelHeading={t("accountOwner.additionalDocuments")}
                state={state}
                t={t}
              />
            </div>
            <div className={`d-md-flex ${styles["mt-40"]}`}>
              <button
                className={`button button-round button-shadow mr-md-4 mb-3 w-sm-100${formDisbaled ? " d-flex gap-xs align-items-center" : ""}`}
                title={t("save")}
                type="submit"
                disabled={formDisbaled}
              >
                {formDisbaled && <LoaderIcon />}
                {t("save")}
              </button>
              <button
                className="mb-md-3 button button-round button-border button-dark btn-mobile-link"
                title={t("cancel")}
                onClick={closeModal}
              >
                {t("cancel")}
              </button>
            </div>
          </form>
        </ModalBody>
      </Modal>
      {isCredentialNameModalOpen && (
        <SelectCredentialNameModal
          isOpen={isCredentialNameModalOpen}
          closeModal={() => setIsCredentialNameModalOpen(false)}
          ownerId={selectedId}
          selectedCredential={selectedCredential}
          onSelect={handleCredentialNameSelect}
          canManageNames={canManageCredentialNames}
          showStaffInfoMessage={
            isStaff && !canManageCredentialNames && !addStaffOffice
          }
          onCredentialUpdated={handleCredentialNameUpdated}
          onCredentialDeleted={handleCredentialNameDeleted}
          isFromProfile={isFromProfile}
        />
      )}
    </>
  );
}

export default withTranslation()(AddEditLincenseeModal);
